import { motion } from 'framer-motion';
import { AlertCircle, Zap } from 'lucide-react';
import '../styles/design-tokens.css';

interface KIResultsProps {
  word: string;
  protoRoot: string;
  originLanguage: string;
  originalMeaning: string;
  modernDefinition: string;
  modernUsage: string;
  ancientFrequency: number;
  modernFrequency: number;
  divergence: number;
  divergencePercentage: number;
  inversionFlag: boolean;
  inversionType?: string;
  oracleSummary: string;
  isLoading?: boolean;
}

export function KIResults({
  word,
  protoRoot,
  originLanguage,
  originalMeaning,
  modernDefinition,
  modernUsage,
  ancientFrequency,
  modernFrequency,
  divergence,
  divergencePercentage,
  inversionFlag,
  inversionType,
  oracleSummary,
  isLoading,
}: KIResultsProps) {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  if (isLoading) {
    return (
      <motion.div
        className="space-y-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        {[1, 2, 3].map((i) => (
          <motion.div
            key={i}
            className="glass-panel p-6 h-40"
            animate={{ backgroundPosition: ['0% 0%', '100% 0%'] }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{
              background: 'linear-gradient(90deg, rgba(212,175,55,0.1), rgba(0,229,255,0.1), rgba(212,175,55,0.1))',
              backgroundSize: '200% 100%',
            }}
          />
        ))}
      </motion.div>
    );
  }

  return (
    <motion.div
      className="space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header with Word */}
      <motion.div variants={cardVariants} className="glass-panel-xl p-8">
        <h2 className="text-4xl font-bold text-gold text-glow mb-2">{word.toUpperCase()}</h2>
        <p className="text-sm text-stone-light">
          Decoding frequency shift from {originLanguage}
        </p>
      </motion.div>

      {/* The Root (Ancient) Panel */}
      <motion.div variants={cardVariants} className="glass-panel p-6">
        <h3 className="text-xl font-bold text-gold mb-4 flex items-center gap-2">
          <span>THE ROOT</span>
          <span className="text-xs text-stone-mid">Ancient Origin</span>
        </h3>
        <div className="space-y-3">
          <div>
            <p className="text-xs text-stone-light mb-1">Proto-Root</p>
            <p className="text-lg text-gold">{protoRoot}</p>
          </div>
          <div>
            <p className="text-xs text-stone-light mb-1">Original Language</p>
            <p className="text-sm text-neutral-50">{originLanguage}</p>
          </div>
          <div>
            <p className="text-xs text-stone-light mb-1">Original Meaning</p>
            <p className="text-sm text-neutral-50">{originalMeaning}</p>
          </div>
          <div className="pt-4 border-t border-silk-gold border-opacity-20">
            <p className="text-xs text-stone-light mb-1">Ancient Base 44 Frequency</p>
            <motion.div
              className="inline-block px-4 py-2 bg-stone-dark border border-silk-gold border-opacity-50 rounded-lg"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3 }}
            >
              <p className="text-2xl font-bold text-gold">{ancientFrequency}</p>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* The Modern Overlay Panel */}
      <motion.div variants={cardVariants} className="glass-panel p-6">
        <h3 className="text-xl font-bold text-cyan mb-4 flex items-center gap-2">
          <span>THE MODERN OVERLAY</span>
          <span className="text-xs text-stone-mid">Current Usage</span>
        </h3>
        <div className="space-y-3">
          <div>
            <p className="text-xs text-stone-light mb-1">Modern Definition</p>
            <p className="text-sm text-neutral-50">{modernDefinition}</p>
          </div>
          <div>
            <p className="text-xs text-stone-light mb-1">Social/Digital Usage</p>
            <p className="text-sm text-neutral-50">{modernUsage}</p>
          </div>
          <div className="pt-4 border-t border-silk-cyan border-opacity-20">
            <p className="text-xs text-stone-light mb-1">Modern Base 44 Frequency</p>
            <motion.div
              className="inline-block px-4 py-2 bg-stone-dark border border-silk-cyan border-opacity-50 rounded-lg"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.4 }}
            >
              <p className="text-2xl font-bold text-cyan">{modernFrequency}</p>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* The Shift Analysis */}
      <motion.div
        variants={cardVariants}
        className={`glass-panel p-6 border-2 ${
          inversionFlag
            ? 'border-red-500 border-opacity-40'
            : 'border-silk-gold border-opacity-20'
        }`}
      >
        <h3 className="text-xl font-bold text-cyan mb-4 flex items-center gap-2">
          <Zap size={20} />
          THE SHIFT
        </h3>
        <div className="space-y-4">
          {/* Divergence Gauge */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <p className="text-sm font-semibold text-stone-light">Frequency Divergence</p>
              <p className="text-lg font-bold text-cyan">{divergencePercentage}%</p>
            </div>
            <div className="h-2 bg-stone-dark rounded-full overflow-hidden">
              <motion.div
                className={`h-full ${
                  divergencePercentage > 70
                    ? 'bg-red-500'
                    : divergencePercentage > 40
                      ? 'bg-yellow-500'
                      : 'bg-silk-cyan'
                }`}
                initial={{ width: 0 }}
                animate={{ width: `${divergencePercentage}%` }}
                transition={{ duration: 1, delay: 0.3 }}
              />
            </div>
          </div>

          {/* Inversion Flag */}
          {inversionFlag && (
            <motion.div
              className="p-4 bg-red-500 bg-opacity-10 border border-red-500 border-opacity-30 rounded-lg flex gap-3"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-300">Linguistic Inversion Detected</p>
                <p className="text-xs text-red-200 mt-1">
                  This word's modern meaning has been significantly altered from its original spiritual
                  frequency. Classification: <span className="font-bold uppercase">{inversionType}</span>
                </p>
              </div>
            </motion.div>
          )}

          {/* Divergence Details */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-stone-light mb-1">Absolute Divergence</p>
              <p className="text-xl font-bold text-gold">{divergence}</p>
            </div>
            <div>
              <p className="text-stone-light mb-1">Severity Level</p>
              <p className={`text-xl font-bold ${
                divergencePercentage > 70 ? 'text-red-400' : 
                divergencePercentage > 40 ? 'text-yellow-400' : 
                'text-green-400'
              }`}>
                {divergencePercentage > 70 ? 'SEVERE' : 
                 divergencePercentage > 40 ? 'MODERATE' : 
                 'MINIMAL'}
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Oracle Insight */}
      <motion.div variants={cardVariants} className="glass-panel-xl p-8 bg-gradient-to-b from-stone-dark to-stone-void">
        <h3 className="text-lg font-bold text-gold mb-4">THE ORACLE INSIGHT</h3>
        <motion.p
          className="text-sm leading-relaxed text-neutral-50 italic"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          {oracleSummary}
        </motion.p>
      </motion.div>
    </motion.div>
  );
}
